// Object-Oriented Models for Flights Tracker refactoring

export class Leg {
  constructor({
    id = '',
    itineraryId = '',
    flightNumber = '', // Flight number digits (e.g. 1729)
    airlineCode = 'UA', // Airline carrier code (e.g. UA)
    date = '',
    // Cooldown & Status tracking
    lastRefreshed = null,
    flightyUrl = '',
    departureAirport = '',
    // API-returned schema fields:
    flight_date = null,
    flight_status = 'scheduled',
    departure = null,
    arrival = null,
    airline = null,
    flight = null,
    aircraft = null,
    live = null
  } = {}) {
    this.id = id || `leg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.itineraryId = itineraryId;
    this.flightNumber = (flightNumber || '').trim().toUpperCase();
    this.airlineCode = (airlineCode || 'UA').trim().toUpperCase();
    this.date = date; // User inputted date, e.g. "2026-06-13" or trip day format
 
    this.lastRefreshed = lastRefreshed;
    this.flightyUrl = flightyUrl;
    this.departureAirport = (departureAirport || '').trim().toUpperCase();

    // Aviation Stack API response payload fields (populated on sync)
    this.flight_date = flight_date || date;
    this.flight_status = flight_status || 'scheduled';
    
    this.departure = departure || {
      airport: '',
      timezone: '',
      iata: '',
      icao: '',
      terminal: '',
      gate: '',
      delay: 0,
      scheduled: '',
      estimated: '',
      actual: '',
      estimated_runway: '',
      actual_runway: '',
      baggage: ''
    };

    this.arrival = arrival || {
      airport: '',
      timezone: '',
      iata: '',
      icao: '',
      terminal: '',
      gate: '',
      delay: 0,
      scheduled: '',
      estimated: '',
      actual: '',
      estimated_runway: '',
      actual_runway: '',
      baggage: ''
    };

    this.airline = airline || {
      id: '',
      fleet_average_age: 0,
      airline_id: '',
      callsign: '',
      hub_code: '',
      iata_code: '',
      icao_code: '',
      country_iso2: '',
      date_founded: '',
      iata_prefix_accounting: '',
      airline_name: '',
      country_name: '',
      fleet_size: 0,
      status: '',
      type: ''
    };

    this.flight = flight || {
      number: '',
      iata: '',
      icao: '',
      codeshared: {}
    };

    this.aircraft = aircraft || {
      registration: '',
      iata: '',
      icao: '',
      icao24: ''
    };

    this.live = live || null;
  }

  // Serializes for Firestore database writes
  toFirestore() {
    return {
      id: this.id,
      itineraryId: this.itineraryId,
      flightNumber: this.flightNumber,
      airlineCode: this.airlineCode,
      date: this.date,
      lastRefreshed: this.lastRefreshed,
      flightyUrl: this.flightyUrl,
      departureAirport: this.departureAirport,
      flight_date: this.flight_date,
      flight_status: this.flight_status,
      departure: this.departure,
      arrival: this.arrival,
      airline: this.airline,
      flight: this.flight,
      aircraft: this.aircraft,
      live: this.live
    };
  }

  // Updates API fields after querying Aviation Stack
  updateFromApiResponse(apiData) {
    if (!apiData) return;
    this.flight_date = apiData.flight_date || this.flight_date;
    this.flight_status = apiData.flight_status || this.flight_status;
    this.departure = { ...this.departure, ...apiData.departure };
    this.arrival = { ...this.arrival, ...apiData.arrival };
    this.airline = { ...this.airline, ...apiData.airline };
    this.flight = { ...this.flight, ...apiData.flight };
    this.aircraft = { ...this.aircraft, ...apiData.aircraft };
    this.live = apiData.live ? { ...this.live, ...apiData.live } : null;
    this.lastRefreshed = Date.now();
  }

  // Updates API fields from flightapi.io array payload
  updateFromFlightApi(apiData) {
    if (!apiData || !Array.isArray(apiData)) return;

    // 1. Resolve status from status object in the array
    const statusObj = apiData.find(item => item && item.status);
    if (statusObj && typeof statusObj.status === 'string') {
      this.flight_status = statusObj.status.toLowerCase();
    }

    // 2. Resolve departure details
    const depObj = apiData.find(item => item && item.departure);
    if (depObj && depObj.departure) {
      // Support both direct object or single-element array (just in case)
      const depData = Array.isArray(depObj.departure) ? depObj.departure[0] : depObj.departure;
      
      if (depData) {
        // Resolve terminal & gate directly from separate properties or fall back to splitting
        let termVal = depData.terminal || "";
        let gateVal = depData.gate || "";
        const parsedGate = depData["Terminal - Gate"] || "";
        if (!termVal && !gateVal && parsedGate) {
          const parts = parsedGate.split('-').map(s => s.trim());
          if (parts.length > 1) {
            termVal = parts[0];
            gateVal = parts[1];
          } else if (parsedGate.toLowerCase().includes('gate')) {
            gateVal = parsedGate;
          } else {
            termVal = parsedGate;
          }
        }

        this.departure = {
          airport: depData.airport || depData.Airport || this.departure.airport,
          iata: depData.airportCode || depData.Airport || this.departure.iata,
          scheduled: depData.scheduledTime || depData["Scheduled Time"] || this.departure.scheduled,
          estimated: depData.estimatedTime || depData["Estimated Time"] || this.departure.estimated,
          terminal: termVal || this.departure.terminal,
          gate: gateVal || this.departure.gate,
          delay: this.flight_status.includes("delay") ? 1 : 0
        };
      }
    }

    // 3. Resolve arrival details
    const arrObj = apiData.find(item => item && item.arrival);
    if (arrObj && arrObj.arrival) {
      // Support both direct object or single-element array
      const arrData = Array.isArray(arrObj.arrival) ? arrObj.arrival[0] : arrObj.arrival;
      
      if (arrData) {
        let termVal = arrData.terminal || "";
        let gateVal = arrData.gate || "";
        const parsedGate = arrData["Terminal - Gate"] || "";
        if (!termVal && !gateVal && parsedGate) {
          const parts = parsedGate.split('-').map(s => s.trim());
          if (parts.length > 1) {
            termVal = parts[0];
            gateVal = parts[1];
          } else if (parsedGate.toLowerCase().includes('gate')) {
            gateVal = parsedGate;
          } else {
            termVal = parsedGate;
          }
        }

        this.arrival = {
          airport: arrData.airport || arrData.Airport || this.arrival.airport,
          iata: arrData.airportCode || arrData.Airport || this.arrival.iata,
          scheduled: arrData.scheduledTime || arrData["Scheduled Time"] || this.arrival.scheduled,
          estimated: arrData.estimatedTime || arrData["Estimated Time"] || this.arrival.estimated,
          terminal: termVal || this.arrival.terminal,
          gate: gateVal || this.arrival.gate,
          delay: 0,
          baggage: arrData.baggage || ""
        };
      }
    }

    this.lastRefreshed = Date.now();
  }
}

export class Itinerary {
  constructor({
    id = '',
    passengers = [], // Must be an array of passenger names (strings)
    direction = 'incoming', // incoming or outgoing
    legs = []
  } = {}) {
    this.id = id || `itinerary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.passengers = Array.isArray(passengers) ? passengers : [passengers].filter(Boolean);
    this.direction = direction; // 'incoming' or 'outgoing'
    this.legs = legs.map(l => l instanceof Leg ? l : new Leg(l));
  }

  // Serializes for Firestore database (Itineraries contain ONLY passengers and direction)
  toFirestore() {
    return {
      id: this.id,
      passengers: this.passengers,
      direction: this.direction
    };
  }

  // Resolves the primary/first date of travel
  getTravelDate() {
    if (this.legs.length === 0) return '';
    return this.legs[0].date;
  }

  // Computes dynamic timeline status from child legs
  getTimelineStatus(isDemoMode = false, isTestModeToday = false) {
    if (this.legs.length === 0) {
      return { text: "No Legs", class: "status-scheduled" };
    }

    // Evaluate live/API statuses first
    let anyInFlight = false;
    let allLanded = true;
    let anyDelayed = false;

    this.legs.forEach(leg => {
      const status = (leg.flight_status || '').toLowerCase();
      if (status === 'active' || status === 'in-flight' || status === 'en-route' || status === 'in air' || status === 'started') {
        anyInFlight = true;
      }
      if (status !== 'landed') {
        allLanded = false;
      }
      if (leg.departure && leg.departure.delay > 0) {
        anyDelayed = true;
      }
    });

    if (anyInFlight) return { text: "In-Flight", class: "status-inflight" };
    if (allLanded) return { text: "Landed", class: "status-landed" };
    if (anyDelayed) return { text: "Delayed", class: "status-delayed" };

    return { text: "Scheduled", class: "status-scheduled" };
  }
}
